import { type GameEvent, gameChannel, gameEventSchema } from "@kalaporssi/shared";

export interface RealtimeConfig {
  /** AppSync Events HTTP domain (used as `host` in the auth header). */
  httpDomain: string;
  /** AppSync Events realtime domain (WebSocket). */
  realtimeDomain: string;
  apiKey: string;
}

export type ConnectionStatus = "connecting" | "connected" | "disconnected";

export interface SubscribeOptions {
  code: string;
  onEvent: (e: GameEvent) => void;
  onStatus?: (s: ConnectionStatus) => void;
  /** Injected for tests. */
  WebSocketImpl?: typeof WebSocket;
  random?: () => number;
}

export interface Subscription {
  close(): void;
}

const PROTOCOL = "aws-appsync-event-ws";
const MAX_BACKOFF_MS = 30_000;

function base64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Subscribes to /game/{CODE} over the AppSync Events WebSocket protocol, with keep-alive tracking and
 * jittered exponential reconnect. Events are signals only; callers refetch state on each one (SPEC §9).
 */
export function subscribeGame(cfg: RealtimeConfig, opts: SubscribeOptions): Subscription {
  const WS = opts.WebSocketImpl ?? WebSocket;
  const random = opts.random ?? Math.random;
  const auth = { host: cfg.httpDomain, "x-api-key": cfg.apiKey };
  let ws: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let kaTimer: ReturnType<typeof setTimeout> | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let kaTimeoutMs = 300_000;
  const subId = `g${Math.floor(random() * 1e12).toString(36)}`;

  const status = (s: ConnectionStatus) => opts.onStatus?.(s);

  const armKeepAlive = () => {
    clearTimeout(kaTimer);
    kaTimer = setTimeout(() => ws?.close(), kaTimeoutMs);
  };

  const scheduleReconnect = () => {
    if (closed) return;
    const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** attempt) * (0.5 + random());
    attempt++;
    retryTimer = setTimeout(connect, delay);
  };

  function connect() {
    if (closed) return;
    status("connecting");
    const sock = new WS(`wss://${cfg.realtimeDomain}/event/realtime`, [
      PROTOCOL,
      `header-${base64Url(JSON.stringify(auth))}`,
    ]);
    ws = sock;
    sock.onopen = () => sock.send(JSON.stringify({ type: "connection_init" }));
    sock.onmessage = (m: MessageEvent) => {
      let msg: { type?: string; id?: string; event?: unknown; connectionTimeoutMs?: number };
      try {
        msg = JSON.parse(String(m.data));
      } catch {
        return;
      }
      switch (msg.type) {
        case "connection_ack":
          kaTimeoutMs = msg.connectionTimeoutMs ?? kaTimeoutMs;
          armKeepAlive();
          sock.send(
            JSON.stringify({ type: "subscribe", id: subId, channel: gameChannel(opts.code), authorization: auth }),
          );
          break;
        case "subscribe_success":
          attempt = 0;
          status("connected");
          break;
        case "ka":
          armKeepAlive();
          break;
        case "data":
          if (msg.id === subId) for (const e of parseEvents(msg.event)) opts.onEvent(e);
          break;
        case "subscribe_error":
        case "connection_error":
        case "error":
          sock.close();
          break;
      }
    };
    sock.onclose = () => {
      clearTimeout(kaTimer);
      if (ws === sock) ws = null;
      // After close() the caller no longer cares; a late close event must not report a stale status.
      if (closed) return;
      status("disconnected");
      scheduleReconnect();
    };
    sock.onerror = () => sock.close();
  }

  connect();

  return {
    close() {
      closed = true;
      clearTimeout(kaTimer);
      clearTimeout(retryTimer);
      if (ws && ws.readyState === ws.OPEN) ws.send(JSON.stringify({ type: "unsubscribe", id: subId }));
      ws?.close();
      ws = null;
    },
  };
}

/** `event` is a JSON string (or an array of them, depending on the message shape). */
function parseEvents(raw: unknown): GameEvent[] {
  const items = Array.isArray(raw) ? raw : [raw];
  const out: GameEvent[] = [];
  for (const item of items) {
    try {
      const v = typeof item === "string" ? JSON.parse(item) : item;
      const parsed = gameEventSchema.safeParse(v);
      if (parsed.success) out.push(parsed.data);
    } catch {
      // ignore malformed events; polling covers the gap
    }
  }
  return out;
}
